<template>
  <div class="sena-winners">
    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button color="gray" icon="fa fa-refresh" @click="refetchAll"/>
      </template>

      <!-- Conferência do rateio, faixa a faixa. O banco garante que a soma das
           parcelas de cada faixa é exatamente o prêmio dela (os `resto`
           primeiros bilhetes ganham 1 centavo a mais). Este badge é a prova
           visível disso. -->
      <div class="sena-summary mb-4" v-if="editionRow">
        <span class="sena-summary__item">
          <b>{{ money(editionRow.prize_amount) }}</b> {{ $t('sena.labels.prizeTotal') }}
        </span>
        <span class="sena-summary__item" v-if="winners.length > 0">
          <b>{{ money(paidSum6) }}</b> {{ $t('sena.labels.shareSum') }}
          ({{ $t('sena.labels.prizeTier6') }})
        </span>
        <span class="sena-summary__item">
          <b>{{ money(editionRow.prize_amount_5) }}</b> {{ $t('sena.labels.prizeTotal5') }}
        </span>
        <span class="sena-summary__item" v-if="winners.length > 0">
          <b>{{ money(paidSum5) }}</b> {{ $t('sena.labels.shareSum') }}
          ({{ $t('sena.labels.prizeTier5') }})
        </span>
        <span class="sena-summary__item">
          <va-badge :color="statusBadge.color">
            {{ statusBadge.text }}
          </va-badge>
        </span>
        <span class="sena-summary__item" v-if="winners.length > 0">
          <b>{{ pendingCount }}</b> {{ $t('sena.labels.pendingPayments') }}
        </span>
      </div>

      <va-data-table
        :fields="fields"
        :data="winners"
        :per-page="perPage"
        no-data-label="Nenhum ganhador"
      >
        <template slot="pagamento" slot-scope="props">
          <va-badge :color="props.rowData.payment_status === 'PAID' ? 'success' : 'warning'">
            {{ $t('sena.paymentStatus.' + props.rowData.payment_status) }}
          </va-badge>
        </template>

        <template slot="actions" slot-scope="props">
          <va-popover
            v-if="props.rowData.payment_status !== 'PAID'"
            :message="`${$t('sena.tooltips.markPaid')}`"
            placement="bottom">
            <va-button
              flat
              color="gray"
              icon="fa fa-check"
              @click="openPay(props.rowData)"
            />
          </va-popover>
          <span v-else class="sena-muted">
            {{ $moment(props.rowData.paid_at).format('DD/MM/YYYY') }}
          </span>
        </template>
      </va-data-table>

      <p class="sena-hint mt-3">{{ $t('sena.hints.paymentOutside') }}</p>
    </va-card>

    <va-modal
      v-model="modalPay"
      size="small"
      :title="$t('sena.modals.payTitle')"
      :okText="$t('modals.confirm')"
      :cancelText="$t('modals.cancel')"
      @ok="markPaid"
    >
      <div v-if="payTarget">
        <p>
          {{ $t('sena.modals.payMessage', {
            name: payTarget.client ? payTarget.client.firstName : payTarget.client_id,
            value: money(payTarget.prize_share),
          }) }}
        </p>
        <va-input
          v-model="paymentNote"
          type="textarea"
          :label="$t('sena.forms.paymentNoteLabel')"
          :placeholder="$t('sena.forms.paymentNotePlaceholder')"
        />
      </div>
    </va-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { senaErrorMixin } from './senaErrors'

const perPage = 25

export default {
  name: 'SenaWinners',
  mixins: [senaErrorMixin],
  props: {
    edition: { required: true },
  },
  data () {
    return {
      winners: [],
      editionRow: null,
      perPage,
      modalPay: false,
      payTarget: null,
      paymentNote: '',
    }
  },
  apollo: {
    winnersData: {
      query: gql`
        query SenaWinners($edition: Int!) {
          editionRow: sena_edition_by_pk(id: $edition) {
            id name status settled_at
            prize_amount total_winners
            prize_amount_5 total_winners_5
          }
          winners: sena_winner(where: {sena_edition_id: {_eq: $edition}}, order_by: {id: asc}) {
            id
            client_id
            sena_ticket_id
            prize_tier
            prize_share
            payment_status
            payment_due_date
            paid_at
            payment_note
            client { id firstName lastName email }
          }
        }
      `,
      variables () {
        return { edition: Number(this.edition) }
      },
      manual: true,
      fetchPolicy: 'network-only',
      result ({ data, loading }) {
        if (loading || !data) return
        this.editionRow = data.editionRow
        this.winners = data.winners || []
      },
    },
  },
  computed: {
    cardTitle () {
      return this.editionRow
        ? `${this.$t('sena.cards.winners')} — ${this.editionRow.name}`
        : this.$t('sena.cards.winners')
    },
    paidSum6 () {
      return this.sumOfTier(6)
    },
    paidSum5 () {
      return this.sumOfTier(5)
    },
    // Cada faixa fecha com o prêmio dela. Faixa sem ganhador não paga nada e
    // por isso não entra na conferência — somar as duas num total só esconderia
    // um rateio errado numa delas.
    sumMatches () {
      if (!this.editionRow || this.winners.length === 0) return false
      return [6, 5].every(tier => this.tierMatches(tier))
    },
    statusBadge () {
      if (!this.editionRow) return { color: 'gray', text: '' }
      if (this.winners.length === 0) {
        if (this.editionRow.status === 'FINISHED') {
          return { color: 'info', text: this.$t('sena.labels.noWinners') }
        }
        if (this.editionRow.status === 'CANCELED') {
          return { color: 'gray', text: this.$t('sena.labels.noWinnersCanceled') }
        }
        return { color: 'warning', text: this.$t('sena.labels.editionInProgress') }
      }
      if (this.sumMatches) {
        return { color: 'success', text: this.$t('sena.labels.sumOk') }
      }
      return { color: 'danger', text: this.$t('sena.labels.sumMismatch') }
    },
    pendingCount () {
      return this.winners.filter(w => w.payment_status !== 'PAID').length
    },
    fields () {
      const moment = this.$moment
      const money = this.money
      return [
        { name: 'client_id', title: this.$t('sena.headings.clientId'), width: '100px', dataClass: 'text-center' },
        {
          name: 'prize_tier',
          title: this.$t('sena.headings.tier'),
          width: '110px',
          dataClass: 'text-center',
          callback (value) {
            return Number(value) === 6 ? 'Super SIX (6 acertos)' : 'Top FIVE (5 acertos)'
          },
        },
        {
          name: 'client',
          title: this.$t('sena.headings.name'),
          callback (value) {
            return value ? `${value.firstName || ''} ${value.lastName || ''}`.trim() : '—'
          },
        },
        {
          name: 'prize_share',
          title: this.$t('sena.headings.share'),
          width: '140px',
          callback (value) {
            return money(value)
          },
        },
        {
          name: 'payment_due_date',
          title: this.$t('sena.headings.dueDate'),
          width: '130px',
          callback (value) {
            return moment(value).format('DD/MM/YYYY')
          },
        },
        {
          name: '__slot:pagamento',
          title: this.$t('sena.headings.payment'),
          width: '120px',
          dataClass: 'text-center',
          titleClass: 'text-center',
        },
        {
          name: 'payment_note',
          title: this.$t('sena.headings.note'),
          callback (value) {
            return value || '—'
          },
        },
        {
          name: '__slot:actions',
          title: this.$t('tables.actions'),
          dataClass: 'text-center',
          titleClass: 'text-center',
          width: '110px',
        },
      ]
    },
  },
  methods: {
    sumOfTier (tier) {
      return this.winners
        .filter(w => Number(w.prize_tier) === tier)
        .reduce((acc, w) => acc + Number(w.prize_share), 0)
    },
    tierMatches (tier) {
      const pagos = this.winners.filter(w => Number(w.prize_tier) === tier)
      if (pagos.length === 0) { return true }
      const premio = tier === 6 ? this.editionRow.prize_amount : this.editionRow.prize_amount_5
      // Comparação em centavos: somar floats e comparar com === erra por
      // arredondamento binário mesmo quando o banco está certo.
      return Math.round(this.sumOfTier(tier) * 100) === Math.round(Number(premio) * 100)
    },
    money (v) {
      return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    refetchAll () {
      this.$apollo.queries.winnersData.refetch()
    },
    openPay (row) {
      this.payTarget = row
      this.paymentNote = row.payment_note || ''
      this.modalPay = true
    },
    markPaid () {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation MarkPaid($id: Int!, $note: String) {
              update_sena_winner_by_pk(
                pk_columns: {id: $id},
                _set: {payment_status: PAID, paid_at: "now()", payment_note: $note}
              ) {
                id
                payment_status
                paid_at
              }
            }
          `,
          variables: {
            id: this.payTarget.id,
            note: this.paymentNote || null,
          },
        })
        .then(() => {
          this.payTarget = null
          this.paymentNote = ''
          this.senaToastSuccess('toasts.successUpdate')
          this.refetchAll()
        })
        .catch(error => {
          this.senaToastError(error, 'toasts.errorUpdate')
        })
    },
  },
}
</script>

<style lang="scss">
.sena-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.sena-muted {
  color: #999999;
  font-size: $font-size-mini;
}

.sena-hint {
  font-size: $font-size-mini;
  color: #888888;
}
</style>
